import {
    StyledDecisionSupport,
    StyledEdit,
} from '@/components/decisionSupport/DecisionSupport.styled';
import { LegacyDecisionSupportRecommendations } from '@/components/decisionSupport/LegacyDecisionSupportRecommendations';
import { ClinicianRecommendationFragment } from '@/components/decisionSupport/queries/clinicianRecommendations.gql.types';
import { NotAvailableCell } from '@/components/decisionSupport/tables/NotAvailableCell';
import { RecommendationCell } from '@/components/decisionSupport/tables/RecommendationCell';
import {
    FACTOR_CODES,
    FACTOR_LABELS,
    findClinicianRecommendation,
} from '@/components/decisionSupport/utils/clinicianRecommendations.utils';
import {
    DecisionSupportSelection,
    buildRecommendedColumn,
    getRange,
    useAxleRecommended,
} from '@/components/decisionSupport/utils/decisionSupport.utils';
import { DialogType, useDialog } from '@/dialogs/DialogManager';
import { ManagedAxleDialogBase } from '@/dialogs/ManagedAxleDialog';
import { IconButton } from '@mui/material';
import { type ColumnDef } from '@tanstack/react-table';
import { FC, useCallback, useMemo } from 'react';

const getValueFromRecommendation = (recommendation: ClinicianRecommendationFragment | null) =>
    recommendation?.clinician.id;

type ClinicianRecommendationsTableProps = {
    onClose(): void;
    selectedClinician: DecisionSupportSelection | null;
    setSelectedClinician(selection: DecisionSupportSelection | null): void;
    recommendations: ClinicianRecommendationFragment[];
};

const LegacyClinicianDecisionSupportTable: FC<ClinicianRecommendationsTableProps> = (props) => {
    const { onClose, recommendations, selectedClinician, setSelectedClinician } = props;

    const selectedFragment = useMemo(
        () => findClinicianRecommendation(recommendations, selectedClinician?.id),
        [recommendations, selectedClinician],
    );

    const axleRecommendedClinician = useAxleRecommended(recommendations);

    const data = useMemo(
        () => recommendations.slice().sort((a, b) => a.recommendationRank - b.recommendationRank),
        [recommendations],
    );

    const geographyRange = useMemo(
        () => getRange(data, 'distanceFromFutureVisitsCentroidMi'),
        [data],
    );
    const hasAnyCompensationTypeScore = useMemo(
        () => data.some((rec) => rec.compensationTypeScore !== null),
        [data],
    );

    const columns: ColumnDef<ClinicianRecommendationFragment>[] = useMemo(
        () => [
            buildRecommendedColumn<ClinicianRecommendationFragment>({
                id: 'candidate',
                header: 'Candidate',
                getLabel: (row) => row.clinician.fullName,
                isRecommended: (row) => row.clinician.id === axleRecommendedClinician?.clinician.id,
            }),
            {
                id: FACTOR_CODES.careTeam,
                header: FACTOR_LABELS[FACTOR_CODES.careTeam],
                cell: ({ row }) => <RecommendationCell value={row.original.careTeamScore} />,
            },
            {
                id: FACTOR_CODES.notFull,
                header: FACTOR_LABELS[FACTOR_CODES.notFull],
                cell: ({ row }) => (
                    <RecommendationCell
                        value={
                            row.original.wouldClinicianBeOverfullByAmortizedVisitDuration ? 0 : 100
                        }
                    />
                ),
            },
            {
                id: FACTOR_CODES.distance,
                header: FACTOR_LABELS[FACTOR_CODES.distance],
                cell: ({ row }) => (
                    <RecommendationCell
                        isInverse
                        value={row.original.distanceFromFutureVisitsCentroidMi}
                        range={geographyRange}
                        roundedTo={25}
                    />
                ),
            },
            {
                id: FACTOR_CODES.caseload,
                header: FACTOR_LABELS[FACTOR_CODES.caseload],
                cell: ({ row }) => (
                    <RecommendationCell
                        value={row.original.spareShiftHoursNormalizedScore}
                        range={[0, 100]}
                        roundedTo={25}
                    />
                ),
            },
            ...(hasAnyCompensationTypeScore
                ? [
                      {
                          id: FACTOR_CODES.compensationType,
                          header: FACTOR_LABELS[FACTOR_CODES.compensationType],
                          cell: ({ row }) => {
                              const { compensationTypeScore } = row.original;
                              if (compensationTypeScore === null) {
                                  return <NotAvailableCell />;
                              }
                              return <RecommendationCell value={compensationTypeScore} />;
                          },
                      } satisfies ColumnDef<ClinicianRecommendationFragment>,
                  ]
                : []),
            {
                id: FACTOR_CODES.language,
                header: FACTOR_LABELS[FACTOR_CODES.language],
                cell: ({ row }) => (
                    <RecommendationCell value={row.original.satisfiesLanguagePreference} />
                ),
            },
            {
                id: FACTOR_CODES.gender,
                header: FACTOR_LABELS[FACTOR_CODES.gender],
                cell: ({ row }) => (
                    <RecommendationCell value={row.original.satisfiesGenderPreference} />
                ),
            },
            {
                id: FACTOR_CODES.continuity,
                header: FACTOR_LABELS[FACTOR_CODES.continuity],
                cell: ({ row }) => (
                    <RecommendationCell
                        value={row.original.continuityOfCareScore === 1 ? 100 : 0}
                    />
                ),
            },
        ],
        [axleRecommendedClinician?.clinician.id, geographyRange, hasAnyCompensationTypeScore],
    );

    const onSubmit = useCallback(
        (clinician: ClinicianRecommendationFragment | null) => {
            setSelectedClinician(
                clinician
                    ? { id: clinician.clinician.id, label: clinician.clinician.fullName }
                    : null,
            );
            onClose();
        },
        [onClose, setSelectedClinician],
    );

    return (
        <LegacyDecisionSupportRecommendations
            recommendations={data}
            getValueFromRecommendation={getValueFromRecommendation}
            selection={selectedFragment}
            columns={columns}
            onSubmit={onSubmit}
        />
    );
};

type LegacyClinicianDecisionSupportDialogProps = {
    onClose(): void;
    selectedClinician: DecisionSupportSelection | null;
    setSelectedClinician(selection: DecisionSupportSelection | null): void;
    recommendations: ClinicianRecommendationFragment[];
};

export const LegacyClinicianDecisionSupportDialog: FC<LegacyClinicianDecisionSupportDialogProps> = (
    props,
) => {
    const { onClose, ...rest } = props;

    return (
        <ManagedAxleDialogBase title='Clinician Recommendations' onClose={onClose}>
            {({ onClose }) => <LegacyClinicianDecisionSupportTable onClose={onClose} {...rest} />}
        </ManagedAxleDialogBase>
    );
};

type LegacyClinicianDecisionSupportProps = {
    selectedClinician: DecisionSupportSelection | null;
    setSelectedClinician(selection: DecisionSupportSelection | null): void;
    recommendations: ClinicianRecommendationFragment[];
    disabled?: boolean;
};

export const LegacyClinicianDecisionSupport: FC<LegacyClinicianDecisionSupportProps> = (props) => {
    const { selectedClinician, setSelectedClinician, recommendations, disabled } = props;
    const { openDialog } = useDialog();

    const onClickEdit = useCallback(() => {
        openDialog(DialogType.LegacyClinicianDecisionSupport, {
            selectedClinician,
            setSelectedClinician,
            recommendations,
        });
    }, [openDialog, recommendations, selectedClinician, setSelectedClinician]);

    if (recommendations.length === 0) {
        return null;
    }

    return (
        <StyledDecisionSupport>
            <IconButton onClick={onClickEdit} disabled={disabled}>
                <StyledEdit />
            </IconButton>
        </StyledDecisionSupport>
    );
};
